import { useEffect, useRef, useState } from "react";
import { Fuel, Upload, Trash2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { LineChartMini, VBarChart } from "../components/Charts";
import api, { API } from "../lib/api";

export const FuelSection = ({ vehicleId, canWrite }) => {
  const [data, setData] = useState({ entries: [], stats: {} });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const res = await api.get(`/fuel/${vehicleId}`);
    setData(res.data);
  };
  useEffect(() => {
    load();
  }, [vehicleId]);

  const onFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/fuel/import`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) throw new Error("import");
      const j = await res.json();
      toast.success(`${j.imported} ligne(s) importée(s)`);
      if (j.unknown_immatriculations?.length)
        toast.info(`Immatriculations inconnues ignorées: ${j.unknown_immatriculations.join(", ")}`);
      await load();
    } catch {
      toast.error("Échec de l'import CSV");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const delEntry = async (id) => {
    await api.delete(`/fuel/${id}`);
    await load();
  };

  const s = data.stats || {};
  const chartData = data.entries
    .filter((e) => e.conso_100 != null)
    .map((e) => ({ label: e.date || `${e.kilometrage} km`, conso: e.conso_100 }));
  const monthData = (s.by_month || []).map((m) => {
    const [y, mo] = m.month.split("-");
    return { label: `${mo}/${y.slice(2)}`, montant: m.montant };
  });
  return (
    <div className="nb-card p-6 mt-8 mc-pop" data-testid="fuel-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Fuel size={20} strokeWidth={2.5} className="text-[#4F46E5]" />
          <h3 className="font-heading font-black text-xl">Consommation carburant</h3>
        </div>
        {canWrite && (
          <>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="fuel-file-input" />
            <button
              data-testid="fuel-import-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 bg-[#FBBF24] font-bold px-4 py-2.5 rounded-xl nb-btn disabled:opacity-60"
            >
              <Upload strokeWidth={2.5} size={18} /> {uploading ? "Import..." : "Importer CSV"}
            </button>
          </>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="p-3 rounded-xl border-2 border-slate-900 bg-white">
          <p className="text-xs font-bold uppercase text-slate-500">Conso moy.</p>
          <p className="font-heading font-black text-xl">{s.avg_conso != null ? `${s.avg_conso} L/100` : "—"}</p>
        </div>
        <div className="p-3 rounded-xl border-2 border-slate-900 bg-white">
          <p className="text-xs font-bold uppercase text-slate-500">Total litres</p>
          <p className="font-heading font-black text-xl">{s.total_litres ?? 0} L</p>
        </div>
        <div className="p-3 rounded-xl border-2 border-slate-900 bg-white">
          <p className="text-xs font-bold uppercase text-slate-500">Total €</p>
          <p className="font-heading font-black text-xl">{s.total_montant ?? 0} €</p>
        </div>
        <div className={`p-3 rounded-xl border-2 border-slate-900 ${s.over_count ? "bg-[#FB923C]" : "bg-white"}`}>
          <p className="text-xs font-bold uppercase text-slate-700">Surconso.</p>
          <p className="font-heading font-black text-xl">{s.over_count ?? 0} plein(s)</p>
        </div>
      </div>

      {s.by_year && s.by_year.length > 0 && (
        <div className="mb-5" data-testid="fuel-by-year">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Budget carburant annuel</p>
          <div className="flex flex-wrap gap-2">
            {s.by_year.map((y) => (
              <div key={y.year} data-testid={`fuel-year-${y.year}`} className="px-3 py-2 rounded-xl border-2 border-slate-900 bg-[#A5B4FC]/40">
                <span className="font-heading font-black">{y.year}</span>
                <span className="ml-2 font-bold">{y.montant} €</span>
                <span className="ml-2 text-slate-600 text-sm font-medium">{y.litres} L · {y.pleins} plein(s)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.entries.length === 0 ? (
        <p className="text-slate-400 font-medium text-sm py-2">
          Aucune donnée. Importez un CSV (colonnes : Immatriculation, Date, Litres, [Type], [Kilométrage], [Montant]).
        </p>
      ) : (
        <>
          {monthData.length > 0 && (
            <div className="mb-5 p-4 rounded-xl border-2 border-slate-900 bg-white" data-testid="fuel-month-chart">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Budget carburant mensuel (€)</p>
              <VBarChart data={monthData.map((d) => ({ label: d.label, value: d.montant }))} height={180} unit=" €" />
            </div>
          )}
          {chartData.length >= 2 && (
            <div className="mb-5 p-4 rounded-xl border-2 border-slate-900 bg-white" data-testid="fuel-chart">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Évolution de la consommation (L/100 km)</p>
              <LineChartMini
                data={chartData.map((d) => ({ label: d.label, value: d.conso }))}
                height={220}
                unit=" L"
                refs={[
                  ...(s.avg_conso != null ? [{ y: s.avg_conso, color: "#64748b", label: "Moy." }] : []),
                  ...(s.conso_reference != null ? [{ y: s.conso_reference, color: "#EF4444", label: "Réf." }] : []),
                ]}
              />
            </div>
          )}
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b-2 border-slate-900">
                <th className="py-2 pr-3 font-bold uppercase text-xs text-slate-500">Date</th>
                <th className="py-2 pr-3 font-bold uppercase text-xs text-slate-500">Litres</th>
                <th className="py-2 pr-3 font-bold uppercase text-xs text-slate-500">Km</th>
                <th className="py-2 pr-3 font-bold uppercase text-xs text-slate-500">L/100</th>
                <th className="py-2 pr-3 font-bold uppercase text-xs text-slate-500">Type</th>
                <th className="py-2 pr-3 font-bold uppercase text-xs text-slate-500">€</th>
                {canWrite && <th></th>}
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} data-testid={`fuel-row-${e.id}`} className={`border-b border-slate-100 ${e.over_avg || e.over_ref ? "bg-[#FB923C]/20" : ""}`}>
                  <td className="py-2 pr-3 font-mono-plate">{e.date || "—"}</td>
                  <td className="py-2 pr-3">{e.litres}</td>
                  <td className="py-2 pr-3">{e.kilometrage ? e.kilometrage.toLocaleString("fr-FR") : "—"}</td>
                  <td className="py-2 pr-3 font-bold">
                    {e.conso_100 != null ? (
                      <span className="inline-flex items-center gap-1">
                        {e.conso_100}
                        {(e.over_avg || e.over_ref) && <TriangleAlert size={14} strokeWidth={2.5} className="text-[#EF4444]" />}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="py-2 pr-3 capitalize">{e.type || "—"}</td>
                  <td className="py-2 pr-3">{e.montant != null ? `${e.montant} €` : "—"}</td>
                  {canWrite && (
                    <td className="py-2">
                      <button onClick={() => delEntry(e.id)} data-testid={`fuel-del-${e.id}`} className="text-red-600 hover:scale-110 transition-transform">
                        <Trash2 size={16} strokeWidth={2.5} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}
    </div>
  );
};
